<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import RetailApiV2 from '@/api/retailApi/RetailApiV2'
import { useProductStore } from '@/stores/ProductStore'
import SearchCategories from './search/SearchCategories.vue'
import SearchProducts from './search/SearchProducts.vue'
import SearchVoice from './search/SearchVoice.vue'
import SearchArticles from './search/SearchArticles.vue'
import SearchNoResults from './search/SearchNoResults.vue'
import MiButton from '@/components/buttons/MiButton.vue'
import { useModalStore } from '@/stores/ModalStore'
import { useDebounceFn } from '@vueuse/core'
import type { Article } from '@/types/articles'

const { setScrollBar, removeScrollBar } = useModalStore()

interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

defineProps({
  type: {
    type: String,
    default: 'desktop',
    validator: (val: unknown) => typeof val === 'string' && ['desktop', 'mobile'].includes(val),
  },
  isTouchScreen: {
    type: Boolean,
    default: false,
  }
})

interface Product {
  id: number;
  name: string;
  state: 'available' | 'sold' | 'pending';
}

const { t } = useI18n()
const router = useRouter()
const products = ref<Product[]>([])
const categories = ref([])
const articles = ref<Article[] | null>(null)
const searchText = ref('')
const voiceSearchActive = ref(false)
const searchLoading = ref(false)

const productStore = useProductStore()

const crossSell = ref()
const searchedWords = ref()

const searchField = ref<HTMLInputElement | null>(null);

const onShowSearch = async () => {
  crossSell.value = await productStore.loadCrossSell('quantity=6&kind=homepage_favorite_cross_sell');
  searchedWords.value = await productStore.loadMostSearchedWords('cleaned=true');
  searchText.value = '';
  searchField.value?.focus();
  searchCall();
  setScrollBar();
}

const onHideSearch = () => {
  removeScrollBar();
}
const searchChanged = async () => {
  searchLoading.value = true;
  debounceSearching();
}

const searchArticlesCall = async () => {
  if (searchText.value !== '') {
    const { data } = await RetailApiV2.get(`search/articles?q=${searchText.value}&limit=2`)
    articles.value = data.articles;
  } else { articles.value = null; }
}

const searchCall = async () => {
  if (searchText.value !== '') {
    const { data } = await RetailApiV2.get(`search?q=${searchText.value}&limit=8&variant=modest`)
    products.value = data.products
    categories.value = data.categories
  } else {
    products.value = []
    categories.value = []
  }
  searchLoading.value = false;
}

const debounceSearching = useDebounceFn(
  () => {
    searchArticlesCall();
    searchCall();
  },
  100,
  { maxWait: 15_000 }
)

const goToSearchView = () => {
  const url = router.resolve({ name: 'searchPath', params: { search: searchText.value } })
  window.location.href = url.href
}

const sortSearchByAvailability = () => {
  products.value.sort((a, b) => Number(b.state === 'available') - Number(a.state === 'available'));
}

const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const recognition = SpeechRecognition ? new SpeechRecognition() : null;
if (SpeechRecognition) {
  if (recognition) {
    searchText.value = '';
    recognition.lang = navigator.language;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      searchText.value = event.results[0][0].transcript;
      searchArticlesCall();
      searchCall();
      recognition.stop();
      voiceSearchActive.value = false;
    };

    recognition.onerror = () => {
      voiceSearchActive.value = false;
    };

    recognition.onend = () => {
      voiceSearchActive.value = false;
    };
  }
  recognition.continuous = false;
}
</script>

<template>
  <VDropdown
    theme="navItem"
    :class="{'w-100': type === 'mobile'}"
    :positioning-disabled="type === 'mobile'"
    popper-class="navItem-max search-mobile-nav"
    @apply-show="onShowSearch"
    @apply-hide="onHideSearch"
  >
    <button
      v-if="type === 'desktop'"
      class="btn text-primary font-medium bg-white xl:pl-4 xl:pr-5 px-2 py-2 py-2 border-0 rounded-pill"
    >
      <i class="mi-search xl:icon-14 icon-18" />
      <span class="hidden xl:inline ml-1">{{ t('search') }}</span>
    </button>
    <Button
      v-else
      class="btn p-0 m-0 flex flex-col no-decoration items-center w-100 shadow-none"
    >
      <i class="mi-search icon-24" />
      <span class="text-xs mt-1">{{ t('search') }}</span>
    </Button>
    <template #popper>
      <div
        class="overflow-x-clip flex justify-center"
        :class="{'w-100': type === 'mobile'}"
      >
        <div
          class="inner-container h-100 flex flex-col"
          :class="{ 'py-5': type !== 'mobile' }"
        >
          <div
            class="search-box-wrap w-100 sticky bottom-0 left-0 zindex-10 flex flex-row items-center px-4 lg:px-6"
            :class="{'order-3 w-100 py-3 shadow-mobile': type === 'mobile'}"
          >
            <MiButton
              v-if="type === 'mobile'"
              v-close-popper
              icon="mi-arrow-l"
              size="sm"
              variant="text"
              rounded
              severity="primary"
            />
            <div
              class="search-box flex items-center"
              :class="type === 'mobile' ? 'pl-3 pr-2 w-100 ml-2' : 'w-50'"
            >
              <i class="mi-search icon-16 lg:icon-40 text-primary border-b" />
              <div class="input-group flex ml-3 lg:ml-4 relative items-center">
                <div class="input-search relative">
                  <input
                    ref="searchField"
                    v-model="searchText"
                    type="search"
                    class="bg-transparent text-primary font-medium py-2 w-100"
                    :class="{'opacity-0' : voiceSearchActive}"
                    :placeholder="type === 'mobile' ? t('search_quote_mobile') : t('search_quote')"
                    :aria-label="t('search_quote')"
                    autocomplete="off"
                    inputmode="search"
                    @keydown.enter="goToSearchView"
                    @input="searchChanged"
                  >
                  <span
                    v-if="voiceSearchActive"
                    class="voice-search-label absolute text-nowrap"
                  >
                    {{ t('voice_search_active') }}
                  </span>
                </div>
                <MiButton
                  v-if="searchText !== '' && !voiceSearchActive"
                  :class="{'mr-2': type !== 'mobile'}"
                  icon="mi-times"
                  size="sm"
                  variant="text"
                  rounded
                  severity="primary"
                  @click="() => {
                    searchText = '';
                    searchChanged();
                  }"
                />
                <MiButton
                  v-if="!isTouchScreen"
                  :icon="voiceSearchActive ? 'mi-times': 'mi-microphone'"
                  size="sm"
                  rounded
                  :severity="voiceSearchActive ? 'danger': 'white'"
                  @click="() => {
                    voiceSearchActive = !voiceSearchActive;
                    if (voiceSearchActive) { recognition.start();
                    } else { recognition.stop();}
                  }"
                />
              </div>
            </div>
            <div
              v-if="false"
              class="ml-4 lg:flex hidden"
            >
              <MiButton
                v-if="products.length > 0"
                severity="white"
                :label="t('sort_by_availability')"
                class="ml-4"
                size="sm"
                @click="sortSearchByAvailability"
              />
            </div>
          </div>
          <div
            v-if="false"
            class="flex lg:hidden sticky left-0 zindex-11 p-2"
            :class="{'order-2 btn-sort-by-availability': type === 'mobile'}"
          >
            <MiButton
              v-if="products.length > 0"
              severity="white"
              :label="t('sort_by_availability')"
              class=""
              size="sm"
              @click="sortSearchByAvailability"
            />
          </div>
          <div
            class="flex flex-col flex-1 px-4 lg:px-6"
            :class="type === 'mobile' ? 'order-1 mobile-search py-5' : 'mt-5'"
          >
            <SearchVoice v-if="voiceSearchActive" />
            <template v-else>
              <div
                v-if="products.length === 0 && !(searchText && products.length === 0 && !searchLoading)"
                class="flex flex-col mb-2"
              >
                <h4 class="text-lg text-primary mb-3">
                  {{ t('most_searched_words') }}
                </h4>
                <ul class="list-unstyled flex flex-wrap text-primary">
                  <li
                    v-for="(item, index) in searchedWords"
                    :key="index"
                  >
                    <button
                      class="rounded-pill px-2 py-1 text-xs mr-2 mb-2 bg-primary-300 text-primary font-medium border-0 text-nowrap"
                      @click="() => {
                        searchText = item;
                        searchCall();
                      }"
                    >
                      {{ item }}
                    </button>
                  </li>
                </ul>
                <hr
                  v-if="type === 'mobile'"
                  class="mt-0 mb-2 border-primary opacity-50 w-100"
                >
              </div>
              <div class="flex flex-col xl:flex-row">
                <div class="flex flex-col categories-col xl:pr-4 order-2 xl:order-1 mt-7 xl:mt-0">
                  <h4 class="text-lg text-primary mb-3">
                    {{ categories.length > 0 ? t('categories_results') : t('favorite_categories') }}
                  </h4>
                  <SearchCategories :categories-array="categories" />
                </div>
                <div 
                  class="flex order-1 xl:order-2 w-full"
                  :class="isTouchScreen ? 'flex-col' : 'flex-row'"
                >
                  <SearchNoResults
                    v-if="searchText && products.length === 0 && !searchLoading"
                    :type="type"
                    :is-touch-screen="isTouchScreen"
                  />
                  <div
                    class="flex flex-col"
                    :class="{'w-100': type === 'mobile'}"
                  >
                    <div class="flex flex-row items-end mb-3">
                      <h4 class="text-lg text-primary mb-0">
                        {{ products.length > 0 ? t('results') : t('recommended') }}
                      </h4>
                      <RouterLink
                        v-if="products.length > 0 && !isTouchScreen"
                        v-close-popper
                        :to="{ name: 'searchPath', params: { search: searchText } }"
                        class="ml-3 text-primary-700 text-sm"
                      >
                        <strong class="underline mr-1">{{ t('all_products') }}</strong>
                        <i class="mi-arrow-r icon-12" />
                      </RouterLink>
                    </div>
                    <SearchProducts
                      :no-results="searchText !== '' && products.length === 0 && !searchLoading"
                      :products="products.length > 0 ? products : crossSell"
                      :is-cross-sell="!(products.length > 0)"
                      :is-touch-screen="isTouchScreen"
                    />
                  </div>
                  <SearchArticles
                    v-if="searchText && articles && !searchLoading"
                    :articles="articles"
                  />
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </template>
  </VDropdown>
</template>

<style lang="scss">
  .btn-sort-by-availability {
    bottom: 76px;
  }
  .overflow-x-clip {
    overflow-x: clip;
  }
  .flex-1 { flex: 1; }
  .search-box-wrap {
    background-color: #F8F3EB;

    &.shadow-mobile {
      box-shadow: 0 -10px 20px rgba(33, 14, 1, 0.1);
    }
    // padding-bottom: env(safe-area-inset-bottom);
  }
  .search-box {
    @media screen and (max-width: 992px) {
        border: none;
        background-color: $white;
        border-radius: 50rem;
        border: 1px solid #cccccc;
      }
    .btn-voice-search {
      width: 32px;
      height: 32px;
    }
    .input-group {
      border-bottom: 1px solid $primary-color-800;
      @media screen and (max-width: 992px) {
        border: none;
      }
    }
    .input-search {
      flex: 1;

      input {
        border: none;
        outline: none;

        &::-webkit-search-decoration,
        &::-webkit-search-cancel-button,
        &::-webkit-search-results-button,
        &::-webkit-search-results-decoration {
          display: none;
        }

        &::placeholder {
          color: rgba(79 43 27 / 0.5);
          opacity: 1;
        }

        &::-ms-input-placeholder {
          color: rgba(79 43 27 / 0.5);
        }
      }
    }
  }
  .categories-col {
    flex: 0 0 274px;
  }
  .voice-search-label {
    background: -webkit-linear-gradient(#9A2E34, #B5633E);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }
  @media screen and (max-width: 1340px) {
    .categories-col {flex: 0 0 238px;}
  }
</style>

<i18n>
  {
    "cs": {
    "all_products": "Všechny produkty",
    "categories_results": "Výsledky kategorií",
    "favorite_categories": "Oblíbené kategorie",
    "most_searched_words": "Nejvyhledávanější výrazy",
    "recommended": "Doporučené",
    "results": "Výsledky vyhledávání",
    "search": "Hledat",
    "search_quote": "Na jaké křupání máte dneska chuť?",
    "search_quote_mobile": "Hledat křupání",
    "sort_by_availability": "Seřadit dle dostupnosti",
    "voice_search_active": "Probíhá hlasové vyhledávání..."
},
    "sk": {
    "all_products": "Všetky produkty",
    "categories_results": "Výsledky kategórií",
    "favorite_categories": "Obľúbené kategórie",
    "most_searched_words": "Najvyhľadávanejšie výrazy",
    "recommended": "Odporúčané",
    "results": "Výsledky vyhľadávania",
    "search": "Hľadať",
    "search_quote": "Na aké chrumkanie máte dnes chuť?",
    "search_quote_mobile": "Hľadať chrumkanie",
    "sort_by_availability": "Zoradiť podľa dostupnosti",
    "voice_search_active": "Prebieha hlasové vyhľadávanie..."
},
    "pl": {
    "all_products": "Wszystkie produkty",
    "categories_results": "Wyniki kategorii",
    "favorite_categories": "Popularne kategorie",
    "most_searched_words": "Najczęściej wyszukiwane hasła",
    "recommended": "Polecane",
    "results": "Wyniki wyszukiwania",
    "search": "Szukaj",
    "search_quote": "Na co masz dziś ochotę?",
    "search_quote_mobile": "Znajdź coś do schrupania",
    "sort_by_availability": "Sortuj według dostępności",
    "voice_search_active": "Trwa wyszukiwanie głosowe..."
},
    "hu": {
    "all_products": "Minden termék",
    "categories_results": "Kategóriák eredményei",
    "favorite_categories": "Kedvenc kategóriák",
    "most_searched_words": "Leggyakrabban keresett",
    "recommended": "Ajánlott",
    "results": "Keresési eredmények",
    "search": "Keresés",
    "search_quote": "Milyen ropogós finomságra vágysz ma?",
    "search_quote_mobile": "Ropogtatás keresése",
    "sort_by_availability": "Elérhetőség szerint",
    "voice_search_active": "Hangkeresés folyamatban..."
},
    "de": {
    "all_products": "Alle Produkte",
    "categories_results": "Kategorie-Ergebnisse",
    "favorite_categories": "Beliebte Kategorien",
    "most_searched_words": "Meistgesuchte Begriffe",
    "recommended": "Empfohlen",
    "results": "Suchergebnisse",
    "search": "Suchen",
    "search_quote": "Auf welche Leckerei hast du heute Lust?",
    "search_quote_mobile": "Leckerei suchen",
    "sort_by_availability": "Nach Verfügbarkeit sortieren",
    "voice_search_active": "Sprachsuche läuft..."
},
    "at": {
    "all_products": "Alle Produkte",
    "categories_results": "Kategorie-Ergebnisse",
    "favorite_categories": "Beliebte Kategorien",
    "most_searched_words": "Meistgesuchte Begriffe",
    "recommended": "Empfohlen",
    "results": "Suchergebnisse",
    "search": "Suchen",
    "search_quote": "Auf welche Leckerei hast du heute Lust?",
    "search_quote_mobile": "Leckerei suchen",
    "sort_by_availability": "Nach Verfügbarkeit sortieren",
    "voice_search_active": "Sprachsuche läuft..."
},
    "ro": {
    "all_products": "Toate produsele",
    "categories_results": "Rezultatele categoriilor",
    "favorite_categories": "Categorii populare",
    "most_searched_words": "Cei mai căutați termeni",
    "recommended": "Recomandate",
    "results": "Rezultatele căutării",
    "search": "Căutare",
    "search_quote": "Ce gustare crocantă vă tentează astăzi?",
    "search_quote_mobile": "Caută gustări crocante",
    "sort_by_availability": "Sortează după disponibilitate",
    "voice_search_active": "Se efectuează căutarea vocală…"
},
    "fr": {
    "all_products": "Tous les produits",
    "categories_results": "Résultats des catégories",
    "favorite_categories": "Favoris",
    "most_searched_words": "Recherches les plus populaires",
    "recommended": "Recommandé",
    "results": "Résultats de recherche",
    "search": "Rechercher",
    "search_quote": "De quelle gourmandise croustillante as-tu envie aujourd’hui ?",
    "search_quote_mobile": "Rechercher une gourmandise croustillante",
    "sort_by_availability": "Trier par disponibilité",
    "voice_search_active": "Recherche vocale en cours..."
}
  }
</i18n>